import { Undefinable } from '@symfa-inc/providence-types';
import { createReducer, on } from '../../core/utils/store';
import {
  AccountingDetails,
  JurisdictionDetails,
  PayloadAndState,
  PermittingSummaryData,
  ProjectManagementDetails,
  SiteBrowse,
  SOFToolDetails,
} from '../../models/interfaces';
import { ProjectDetailsWithoutCategories } from '../../models/types';
import { BreadcrumbsActions } from '../actions/breadcrumbs.actions';

export interface BreadcrumbsState {
  breadcrumbs: Record<string, Undefinable<string | number>>;
}

const initialState: BreadcrumbsState = {
  breadcrumbs: {},
};

export const reducer = createReducer(
  initialState,
  on(
    BreadcrumbsActions.site,
    ({
      payload: { siteNumber },
    }: PayloadAndState<SiteBrowse, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
      },
    }),
  ),
  on(
    BreadcrumbsActions.project,
    ({
      payload: {
        site: { siteNumber },
        projectBundleID,
      },
    }: PayloadAndState<ProjectDetailsWithoutCategories, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.engineering,
    ({
      payload: {
        site: { siteNumber },
        projectBundleID,
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.SOFTool,
    ({
      payload: {
        generalInfo: {
          site: { siteNumber },
          projectBundleID,
        },
      },
    }: PayloadAndState<SOFToolDetails, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.scoping,
    ({
      payload: {
        projectBundleID,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'RAD'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Engineering.siteAudit,
    ({
      payload: {
        projectBundleID,
        site: { siteNumber },
      },
    }: PayloadAndState<ProjectDetailsWithoutCategories, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.leasing,
    ({
      payload: {
        projectBundleID,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectDetailsWithoutCategories, 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Permitting.permitting,
    ({
      payload: {
        project: { projectBundleID },
        site: { siteNumber },
      },
    }: PayloadAndState<PermittingSummaryData, BreadcrumbsState>) => ({
      breadcrumbs: {
        siteNumber,
        projectBundleID,
      },
    }),
  ),
  on(
    BreadcrumbsActions.Permitting.jurisdiction,
    ({
      payload: { jurisdictionName },
    }: PayloadAndState<JurisdictionDetails, BreadcrumbsState>) => ({
      breadcrumbs: { jurisdictionName },
    }),
  ),
  on(
    BreadcrumbsActions.projectManagement,
    ({
      payload: {
        projectBundleID,
        site: { siteNumber },
      },
    }: PayloadAndState<
      Omit<ProjectManagementDetails, 'categories' | 'paceTasks'>,
      BreadcrumbsState
    >) => ({
      breadcrumbs: { projectBundleID, siteNumber },
    }),
  ),
  on(
    BreadcrumbsActions.accounting,
    ({
      payload: {
        projectData: {
          projectBundleID,
          site: { siteNumber },
        },
      },
    }: PayloadAndState<AccountingDetails, BreadcrumbsState>) => ({
      breadcrumbs: { projectBundleID, siteNumber },
    }),
  ),
);
